import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import Section from '../components/common/Section';
import Button from '../components/common/Button';

interface Draft {
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  cover_image_url: string;
  published: boolean;
}

const emptyDraft: Draft = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  cover_image_url: '',
  published: false,
};

const Form = styled.form`
  max-width: 900px;
  margin: 0 auto;
  color: var(--text-color);

  label { display: block; margin: 1rem 0 0.5rem; font-weight: 500; }
  input, textarea {
    width: 100%;
    padding: 0.75rem;
    border-radius: var(--border-radius);
    border: 1px solid rgba(255,255,255,0.15);
    background: var(--background-dark, #111);
    color: var(--text-color);
    font-size: 1rem;
    font-family: inherit;
  }
  textarea { min-height: 400px; font-family: ui-monospace, Menlo, Consolas, monospace; }
  .row { display: flex; gap: 1rem; align-items: center; flex-wrap: wrap; margin-top: 1.5rem; }
  .error { color: #ff6b6b; margin-top: 1rem; }
  .cover-preview {
    margin-top: 0.5rem;
    max-width: 320px;
    border-radius: var(--border-radius);
    display: block;
  }
  .upload-row { display: flex; gap: 0.5rem; align-items: center; margin-top: 0.5rem; }
`;

const AdminEditPage: React.FC<{ mode: 'new' | 'edit' }> = ({ mode }) => {
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [loading, setLoading] = useState(mode === 'edit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (mode !== 'edit' || !id) return;
    const res = await fetch(`/api/admin/posts/${id}`);
    if (res.status === 401) return nav('/admin/login');
    if (!res.ok) {
      setError('Could not load post');
      setLoading(false);
      return;
    }
    const data = await res.json();
    const p = data.post;
    setDraft({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt || '',
      body: p.body,
      cover_image_url: p.cover_image_url || '',
      published: !!p.published,
    });
    setLoading(false);
  }, [id, mode, nav]);

  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));

  const uploadImage = async (file: File, into: 'cover' | 'body') => {
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/admin/upload', { method: 'POST', body: fd });
      if (res.status === 401) return nav('/admin/login');
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Upload failed');
        return;
      }
      if (into === 'cover') {
        set('cover_image_url', data.url);
      } else {
        setDraft((d) => ({ ...d, body: d.body + `\n\n![](${data.url})\n\n` }));
      }
    } finally {
      setUploading(false);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const method = mode === 'edit' ? 'PUT' : 'POST';
      const url = mode === 'edit' ? `/api/admin/posts/${id}` : '/api/admin/posts';
      const res = await fetch(url, {
        method,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      });
      if (res.status === 401) return nav('/admin/login');
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Save failed');
        return;
      }
      nav('/admin');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Section background="light" padding="large">
          <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>
        </Section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Section background="light" padding="large">
        <Form onSubmit={save}>
          <h1>{mode === 'edit' ? 'Edit post' : 'New post'}</h1>

          <label>Title</label>
          <input value={draft.title} onChange={(e) => set('title', e.target.value)} required />

          <label>Slug (leave blank to auto-generate from title)</label>
          <input value={draft.slug} onChange={(e) => set('slug', e.target.value)} placeholder="my-post-title" />

          <label>Excerpt (short summary for the blog list card)</label>
          <input value={draft.excerpt} onChange={(e) => set('excerpt', e.target.value)} />

          <label>Cover image</label>
          <div className="upload-row">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], 'cover')}
              disabled={uploading}
            />
            {uploading && <span>Uploading…</span>}
          </div>
          {draft.cover_image_url && (
            <img className="cover-preview" src={draft.cover_image_url} alt="cover preview" />
          )}
          <input
            style={{ marginTop: '0.5rem' }}
            value={draft.cover_image_url}
            onChange={(e) => set('cover_image_url', e.target.value)}
            placeholder="Or paste an image URL"
          />

          <label>Body (Markdown supported)</label>
          <div className="upload-row">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0], 'body')}
              disabled={uploading}
            />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Uploaded images are appended to the body as markdown.
            </span>
          </div>
          <textarea value={draft.body} onChange={(e) => set('body', e.target.value)} required />

          <div className="row">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={draft.published}
                onChange={(e) => set('published', e.target.checked)}
              />
              Published
            </label>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={() => nav('/admin')}>
              Cancel
            </Button>
          </div>

          {error && <div className="error">{error}</div>}
        </Form>
      </Section>
    </Layout>
  );
};

export default AdminEditPage;
